import { GetterTree } from 'vuex'

import { Portion, PortionContext } from '@/models'

import { orderByLocale } from '@/utils'

import { RootState } from '../../state'
import { MenuState } from '../state'

const getters: GetterTree<MenuState, RootState> = {
  /**
   * Returns a Portion by its context, or null if not found.
   *
   * It throws if the Portion is not loaded but its id is in an Item relation.
   *
   * @param context ItemContext to be found.
   */
  portionByContext: (state) => ({ id, itemId }: PortionContext): Portion | null => {
    const itemsPortions = state.items.portions[itemId]

    if (itemsPortions && itemsPortions.ids.has(id)) {
      const portion = state.portions.elements.find((i) => i.id === id)

      if (portion === undefined) {
        throw new Error(`Portion "${id}" was not found, but it is in the Item "${itemId}" relation`)
      }

      return portion
    }

    return null
  },

  /**
   * Returns all Portions in an Item.
   *
   * It throws if the Portion is not loaded but its id is in an Item relation.
   *
   * @param itemId Item id to search.
   */
  portionsByItemId: (state) => (itemId: number): Portion[] => {
    const itemPortions = state.items.portions[itemId]

    if (itemPortions === undefined) {
      return []
    }

    const portions: Portion[] = []

    for (const id of itemPortions.ids) {
      const portion = state.portions.elements.find((i) => i.id === id)

      if (portion === undefined) {
        throw new Error(`Portion "${id}" was not found, but it is in the Item "${itemId}" relation`)
      }

      portions.push(portion)
    }

    return portions
  },

  /**
   * Returns Portions in an Item ordered by name.
   *
   * @param itemId Item id to search.
   */
  orderedPortions: (_, getters) => (itemId: number): Portion[] => {
    return orderByLocale(getters.portionsByItemId(itemId), 'name')
  }
}

export default getters
